import { radius } from '@alison/ui';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { type EmptyRun, emptyBodyOf } from './emptyResult.js';
import { Pill } from './Pill.js';
import type { EmptyState } from './workflows.types.js';

const ILLUSTRATION_SRC = '/images/workflows/fatigue-all-clear.svg';
const CHECK_SRC = '/images/workflows/all-clear-check.svg';

const STATE_SX = { gap: 3, pt: 5, alignItems: 'center', textAlign: 'center' };
const TITLE_SX = { color: 'background.icon' };
const BODY_SX = { maxWidth: 520, fontSize: 14, lineHeight: 1.5, color: 'text.secondary' };

interface EmptyResultStateProps {
  copy: EmptyState;
  run: EmptyRun;
  onEdit?: () => void;
}

function Chips({ chips, label }: { chips: string[]; label: string }): ReactNode {
  return (
    <Stack
      component="ul"
      direction="row"
      {...(label === '' ? {} : { 'aria-label': label })}
      sx={{
        m: 0,
        px: 3.6,
        py: 1.8,
        gap: 3.2,
        flexWrap: 'wrap',
        justifyContent: 'center',
        listStyle: 'none',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'border.strong',
        borderRadius: `${radius.pill}px`,
      }}
    >
      {chips.map((chip) => (
        <Stack key={chip} component="li" direction="row" sx={{ gap: 1.2, alignItems: 'center' }}>
          <Box component="img" src={CHECK_SRC} alt="" width={12} height={12} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'background.icon' }}>
            {chip}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function EmptyResultState({ copy, run, onEdit }: EmptyResultStateProps): ReactNode {
  const chips = run.customised
    ? []
    : (run.preset?.chips ?? []).map((chip) => `${chip.label} ${chip.text}`);

  return (
    <Stack sx={STATE_SX}>
      <Box
        component="img"
        src={ILLUSTRATION_SRC}
        alt=""
        width={280}
        height={150}
        sx={{ maxWidth: '100%', height: 'auto' }}
      />
      {copy.badge === '' ? null : (
        <Pill
          sx={(theme) => ({
            px: 1.2,
            py: 0.8,
            bgcolor: alpha(theme.palette.success.dark, 0.1),
            color: 'success.dark',
            letterSpacing: '0.8px',
          })}
        >
          {copy.badge}
        </Pill>
      )}
      <Typography variant="h1" component="p" sx={TITLE_SX}>
        {copy.title}
      </Typography>
      <Typography sx={BODY_SX}>{emptyBodyOf(copy, run)}</Typography>
      {chips.length === 0 ? null : <Chips chips={chips} label={copy.chipsLabel} />}
      {onEdit === undefined || copy.editCta === '' ? null : (
        <Box sx={{ py: 5 }}>
          <Button variant="outlined" onClick={onEdit}>
            {copy.editCta}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
